import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackIcon, DeleteIcon, EditIconBlue } from 'assets/icons';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import routesPath from 'routes/RoutesPath';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import CustomSwitch from 'components/switch/Switch';
import {
  useDeleteExternalCompanyMutation,
  useLazyGetExternalCompaniesQuery,
  useUpdateExternalCompanyStatusMutation
} from './api';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import Loader from 'components/loader/Loader';
import GenericDeleteModal from 'components/generic-delete-modal/GenericDeleteModal';
import { ExternalCompanyResponse } from './externalCompany.model';
import { CommissionStatus, CommissionType } from '../commission-settings/constants';
import { InfoMesssages } from 'types/infoMessages';
import { If, NoPermissions } from 'components';
import { logAnalyticEvent } from 'utils/analytics';
import { SettingsCleverTapEvents } from '../events';
import { getCommissionBaseLabel } from '../utils';

const ExternalCompanySettings: FC = () => {
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentJobSettingsPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { editJobSource, viewJobSource } = currentJobSettingsPermissions ?? {};
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<ExternalCompanyResponse | null>(null);

  const [getExternalCompanies, { data: companiesData, isLoading }] = useLazyGetExternalCompaniesQuery();

  const [deleteExternalCompany, { isLoading: isDeleting }] = useDeleteExternalCompanyMutation();
  const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateExternalCompanyStatusMutation();

  useEffect(() => {
    if (viewJobSource || editJobSource) {
      getExternalCompanies({ teamId: currentTeam?.id?.toString() || '' });
    }
  }, [currentTeam?.id, viewJobSource, editJobSource]);

  const handleDeleteClick = (company: ExternalCompanyResponse) => {
    setSelectedCompany(company);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCompany || !currentTeam?.id) return;

    try {
      await deleteExternalCompany({
        teamId: currentTeam.id.toString(),
        externalCompanyId: selectedCompany.id
      }).unwrap();
      setShowDeleteModal(false);
      setSelectedCompany(null);
    } catch (error) {
      // Error handling
    }
  };

  const handleStatusChange = async (company: ExternalCompanyResponse) => {
    if (!currentTeam?.id) return;

    const newStatus = company.status === CommissionStatus.ACTIVE ? CommissionStatus.ARCHIVED : CommissionStatus.ACTIVE;

    try {
      await updateStatus({
        teamId: currentTeam.id.toString(),
        externalCompanyId: company.id,
        status: newStatus
      }).unwrap();
      logAnalyticEvent(SettingsCleverTapEvents.webSettingsExternalCompanyStatusChange, {
        externalCompanyId: company.id,
        status: newStatus
      });
    } catch (error) {
      // Error handling
    }
  };

  const handleAddNewProfile = () => {
    navigate(routesPath.ADD_EXTERNAL_COMPANY);
  };

  const handleEditProfile = (companyId: string) => {
    navigate(routesPath.EDIT_EXTERNAL_COMPANY.replace(':id', companyId));
  };

  const getCommissionDisplay = (company: ExternalCompanyResponse): string => {
    if (!(company.commissionValue >= 0) || !company.commissionType) return '-';
    if (company.commissionType === CommissionType.ABSOLUTE) return `$${company.commissionValue} (fixed)`;
    return `${company.commissionValue}% of ${getCommissionBaseLabel(company.commissionApplyOn)}`;
  };

  const handleBackClick = () => {
    navigate(routesPath.SETTINGS, { replace: true });
  };

  if (isLoading) {
    return (
      <div className="mt-6 flex h-[400px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="h-full w-full py-7 px-10">
      <div className="mb-10 flex items-center">
        <BackIcon
          className="path path-stroke-current mr-5 h-5 w-5 cursor-pointer text-primaryText"
          onClick={handleBackClick}
        />
        <div className="text-2xl font-bold text-primaryText">External companies</div>
      </div>

      {/* External Company Profiles Section */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-xl font-bold text-primaryText">External company profiles</div>
          <div className="mt-2 text-17px font-normal text-accentText">
            External companies can be assigned job referrals and have unique commission rules.
          </div>
        </div>
        <CustomButton
          showDisabledReason={!editJobSource}
          disabledReason={InfoMesssages.createExternalCompanyNoPermissionMessage}
          id="add-new-profile"
          type={ButtonType.PRIMARY}
          onClick={handleAddNewProfile}>
          + Add new profile
        </CustomButton>
      </div>

      {/* Table Section */}
      <If condition={viewJobSource || editJobSource}>
        <div className="mt-6 w-full">
          <div className="overflow-hidden rounded-lg border border-borderGray">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sbase font-semibold text-primaryText">Company name</th>
                  <th className="px-6 py-3 text-left text-sbase font-semibold text-primaryText">Primary contact</th>
                  <th className="px-6 py-3 text-left text-sbase font-semibold text-primaryText">
                    Default referral rate
                  </th>
                  <th className="px-6 py-3 text-left text-sbase font-semibold text-primaryText">Active</th>
                  <th className="px-6 py-3 text-left text-sbase font-semibold text-primaryText">Created by</th>
                  <th className="px-6 py-3 text-left text-sbase font-semibold text-primaryText">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {companiesData?.data.length ? (
                  companiesData.data.map(company => (
                    <tr key={company.id}>
                      <td className="px-6 py-3 text-sbase font-semibold text-primaryText">{company.companyName}</td>
                      <td className="px-6 py-3">
                        {company.companyPocFirstname ||
                        company.companyPocLastname ||
                        company.companyPocEmail ||
                        company.companyPocPhone ? (
                          <>
                            <div className="text-sbase text-primaryText">
                              {`${company.companyPocFirstname} ${company.companyPocLastname}`}
                            </div>
                            <div className="text-sbase text-primaryText">{company.companyPocEmail}</div>
                            <div className="text-sbase text-accentText">{company.companyPocPhone}</div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-3 text-sbase text-primaryText">{getCommissionDisplay(company)}</td>
                      <td className="px-6 py-3">
                        <CustomSwitch
                          enabled={company.status === CommissionStatus.ACTIVE}
                          toggleSwitch={() => handleStatusChange(company)}
                          id={`company-status-switch-${company.id}`}
                          disabled={isUpdatingStatus || !editJobSource}
                        />
                      </td>
                      <td className="px-6 py-3 text-sbase text-primaryText">{company.createdBy}</td>
                      <td className="px-6 py-3">
                        {editJobSource && (
                          <div className="flex items-center gap-2">
                            <TooltipWrapper tooltip="Edit company">
                              <button
                                className="rounded-md p-1 text-secondary hover:bg-gray-100"
                                onClick={() => handleEditProfile(company.id)}>
                                <EditIconBlue className="h-4 w-4" />
                              </button>
                            </TooltipWrapper>
                            <TooltipWrapper tooltip="Delete company">
                              <button
                                className="rounded-md p-1 text-secondary hover:bg-gray-100"
                                onClick={() => handleDeleteClick(company)}>
                                <DeleteIcon className="h-4 w-4" />
                              </button>
                            </TooltipWrapper>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-3 text-center text-sbase text-accentText">
                      No external companies found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </If>
      <If condition={!viewJobSource && !editJobSource}>
        <div className="flex h-2/3 flex-col items-center justify-center gap-4 py-16">
          <NoPermissions />
        </div>
      </If>

      {showDeleteModal && selectedCompany && (
        <GenericDeleteModal
          showModal={showDeleteModal}
          isActionInProgress={isDeleting}
          title="Delete External Company"
          subTitle={`Are you sure you want to delete "${selectedCompany.companyName}"? This action cannot be undone.`}
          primaryBtnLabel="Delete"
          primaryBtnId="delete-external-company"
          onPrimaryBtnClick={handleConfirmDelete}
          secondaryBtnLabel="Cancel"
          secondaryBtnId="cancel-delete-external-company"
          onSecondaryBtnClick={() => {
            setShowDeleteModal(false);
            setSelectedCompany(null);
          }}
        />
      )}
    </div>
  );
};

export default ExternalCompanySettings;
